// Definition of the 7 phases of the listing workflow.
// Each task has: id, label, and optional subtasks (some optional).

export const LISTING_STATUSES = [
  "Pre-Listing",
  "Coming Soon",
  "Active",
  "Pending",
  "Closed",
  "Cancelled",
  "Withdrawn",
];

export const STATUS_STYLES = {
  "Pre-Listing": "bg-stone-100 text-stone-700 border-stone-200",
  "Coming Soon": "bg-amber-50 text-amber-800 border-amber-200",
  "Active": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Pending": "bg-orange-50 text-orange-800 border-orange-200",
  "Closed": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Cancelled": "bg-rose-50 text-rose-800 border-rose-200",
  "Withdrawn": "bg-rose-50 text-rose-800 border-rose-200",
};

export const PHASES = [
  {
    id: "p1",
    number: 1,
    title: "Listing Setup",
    description: "Kick off with intake, seller alignment, and the transaction file.",
    tasks: [
      { id: "t1_1", label: "Complete seller intake and property walkthrough" },
      { id: "t1_2", label: "Review seller goals, timing, pricing and estimated net proceeds" },
      { id: "t1_3", label: "Sign Listing Agreement and required agency disclosures" },
      { id: "t1_4", label: "Create transaction file and submit signed documents to Transaction Coordinator" },
      {
        id: "t1_5",
        label: "Confirm property and HOA requirements",
        subtasks: [
          { id: "s1_5_a", label: "Seller contact and occupancy information", optional: true },
          { id: "s1_5_b", label: "HOA information", optional: true },
          { id: "s1_5_c", label: "Order HOA documents for condo, townhome or PUD", optional: true },
        ],
      },
      {
        id: "t1_6",
        label: "Seller Disclosure Package Complete",
        subtasks: [
          { id: "s1_6_a", label: "Disclosures sent for signature" },
          { id: "s1_6_b", label: "Disclosures fully signed by seller" },
          { id: "s1_6_c", label: "Complete Listing Agent Visual Inspection Disclosure (AVID)" },
          { id: "s1_6_d", label: "AVID signed / acknowledged by seller" },
        ],
      },
    ],
  },
  {
    id: "p2",
    number: 2,
    title: "Access and Property Preparation",
    description: "Get the home ready and access dialed in before we shoot.",
    tasks: [
      {
        id: "t2_1",
        label: "Collect and test property access",
        subtasks: [
          { id: "s2_1_a", label: "Keys" },
          { id: "s2_1_b", label: "Remotes", optional: true },
          { id: "s2_1_c", label: "Fobs", optional: true },
          { id: "s2_1_d", label: "Gate codes", optional: true },
          { id: "s2_1_e", label: "Alarm or smart-lock codes", optional: true },
        ],
      },
      { id: "t2_2", label: "Install Supra lockbox" },
      {
        id: "t2_3",
        label: "Coordinate property preparation",
        subtasks: [
          { id: "s2_3_a", label: "Repairs", optional: true },
          { id: "s2_3_b", label: "Cleaning", optional: true },
          { id: "s2_3_c", label: "Landscaping", optional: true },
          { id: "s2_3_d", label: "Staging", optional: true },
        ],
      },
      {
        id: "t2_4",
        label: "Order inspections",
        subtasks: [
          { id: "s2_4_a", label: "Home", optional: true },
          { id: "s2_4_b", label: "Pest / Termite", optional: true },
          { id: "s2_4_c", label: "Roof", optional: true },
          { id: "s2_4_d", label: "Sewer", optional: true },
          { id: "s2_4_e", label: "Chimney", optional: true },
        ],
      },
      {
        id: "t2_5",
        label: "Order reports",
        subtasks: [
          { id: "s2_5_a", label: "Preliminary Title" },
          { id: "s2_5_b", label: "HOA documents, if applicable", optional: true },
        ],
      },
      { id: "t2_6", label: "Complete final walkthrough before photography" },
    ],
  },
  {
    id: "p3",
    number: 3,
    title: "Marketing and Media",
    description: "Photography, story, and the assets that make it shine.",
    tasks: [
      {
        id: "t3_1",
        label: "Order media",
        subtasks: [
          { id: "s3_1_a", label: "Photography" },
          { id: "s3_1_b", label: "Floor plan", optional: true },
          { id: "s3_1_c", label: "Matterport", optional: true },
          { id: "s3_1_d", label: "Drone", optional: true },
          { id: "s3_1_e", label: "Video", optional: true },
        ],
      },
      {
        id: "t3_2",
        label: "Prepare listing marketing",
        subtasks: [
          { id: "s3_2_a", label: "MLS remarks" },
          { id: "s3_2_b", label: "Property features" },
          { id: "s3_2_c", label: "Property website", optional: true },
          { id: "s3_2_d", label: "Flyers" },
          { id: "s3_2_e", label: "Social media", optional: true },
        ],
      },
      { id: "t3_3", label: "Install yard sign" },
    ],
  },
  {
    id: "p4",
    number: 4,
    title: "MLS Launch and Showings",
    description: "Go live and get the world through the front door.",
    tasks: [
      { id: "t4_1", label: "Complete MLS input and upload media" },
      { id: "t4_2", label: "Confirm showing instructions and seller notifications" },
      { id: "t4_3", label: "Activate MLS and verify portal syndication", linkable: true, linkLabel: "MLS Printout Link" },
      { id: "t4_4", label: "Test Supra lockbox and property access" },
      { id: "t4_5", label: "Schedule open houses and showing activity" },
      { id: "t4_6", label: "Disclosure Package Uploaded to MLS for Buyer Review", linkable: true, linkLabel: "Disclosure Package Link" },
    ],
  },
  {
    id: "p5",
    number: 5,
    title: "Open Houses and Marketing",
    description: "Convert visibility into engaged buyers.",
    tasks: [
      { id: "t5_1", label: "Prepare and conduct open houses" },
      { id: "t5_broker_tour", label: "Broker Tour / Caravan" },
      { id: "t5_2", label: "Follow up with visitors and showing agents" },
      { id: "t5_marketing_review", label: "Review and assessment of marketing efforts" },
      { id: "t5_3", label: "Review feedback and strategy with seller" },
    ],
  },
  {
    id: "p6",
    number: 6,
    title: "Offers and Negotiation",
    description: "Qualify buyers, negotiate terms, land the deal.",
    tasks: [
      {
        id: "t5_4",
        label: "Verify buyer qualifications",
        subtasks: [
          { id: "s5_4_a", label: "Pre-approval" },
          { id: "s5_4_b", label: "Proof of funds" },
        ],
      },
      { id: "t5_5", label: "Review and present offers to seller" },
      { id: "t6_counteroffers", label: "Issue and track counteroffers" },
      { id: "t6_acceptance", label: "Obtain offer acceptance" },
      { id: "t5_6", label: "Open escrow and update MLS status" },
    ],
  },
  {
    id: "p7",
    number: 7,
    title: "Escrow and Closing",
    description: "From acceptance to funded and recorded — smoothly.",
    tasks: [
      { id: "t6_1", label: "Send accepted contract to Transaction Coordinator and escrow" },
      { id: "t6_2", label: "Confirm earnest money deposit" },
      { id: "t6_3_a", label: "Inspections Contingency" },
      { id: "t6_3_b", label: "Appraisal Contingency" },
      { id: "t6_3_c", label: "Loan Contingency" },
      { id: "t6_3_e", label: "Insurance Contingency" },
      { id: "t6_3_f", label: "Review of Seller Documents Contingency" },
      { id: "t6_3_g", label: "Preliminary Title Review Contingency" },
      { id: "t6_3_h", label: "HOA / Common Interest Disclosures Contingency" },
      { id: "t6_3_d", label: "All Contingencies Released" },
      { id: "t6_4", label: "Coordinate repair requests and schedule final walkthrough" },
      { id: "t6_4_vp", label: "Obtain fully signed Verification of Property Condition (Form VP) from all parties" },
      { id: "t6_commission_demand", label: "Submit commission demand / instructions to escrow" },
      { id: "t6_5", label: "Confirm loan approval, funding and recording" },
      {
        id: "t6_6",
        label: "Transfer property access items",
        subtasks: [
          { id: "s6_6_a", label: "Keys" },
          { id: "s6_6_b", label: "Remotes", optional: true },
          { id: "s6_6_c", label: "Fobs", optional: true },
          { id: "s6_6_d", label: "Codes", optional: true },
          { id: "s6_6_e", label: "Property instructions", optional: true },
        ],
      },
      { id: "t6_7", label: "Remove lockbox and yard sign; close MLS" },
    ],
  },
  {
    id: "p8",
    number: 8,
    title: "Post-Closing",
    description: "Close strong. Stay in touch. Earn the referral.",
    tasks: [
      { id: "t7_1", label: "Confirm seller received proceeds" },
      { id: "t7_2", label: "Deliver closing gift or thank-you" },
      { id: "t7_3", label: "Request client reviews" },
      { id: "t7_4", label: "Add seller to CRM follow-up" },
      { id: "t7_5", label: "Complete compliance file and archive transaction" },
    ],
  },
];

export const TASK_OFFSETS = {
  // Phase 1 - Listing Setup
  t1_1: -14, t1_2: -13, t1_3: -12, t1_4: -11, t1_5: -10, t1_6: -9,
  // Phase 2 - Access & Prep
  t2_1: -9, t2_2: -7, t2_3: -7, t2_4: -8, t2_5: -8, t2_6: -3,
  // Phase 3 - Marketing & Media
  t3_1: -5, t3_2: -3, t3_3: -1,
  // Phase 4 - MLS Launch
  t4_1: -2, t4_2: -1, t4_3: 0, t4_4: 0, t4_5: 1, t4_6: 0,
  // Phase 5 - Open Houses and Marketing
  t5_1: 3, t5_broker_tour: 2, t5_2: 5, t5_marketing_review: 10, t5_3: 12,
  // Phase 6 - Offers and Negotiation
  t5_4: 10, t5_5: 14, t6_counteroffers: 16, t6_acceptance: 18, t5_6: 21,
  // Phase 7 - Escrow & Closing
  t6_1: 22, t6_2: 24,
  t6_3_a: 27, t6_3_b: 32, t6_3_c: 37,
  t6_3_e: 37, t6_3_f: 37, t6_3_g: 37, t6_3_h: 37,
  t6_3_d: 39,
  t6_4: 40, t6_4_vp: 41, t6_commission_demand: 39, t6_5: 42, t6_6: 43, t6_7: 43,
  // Phase 8 - Post-Closing
  t7_1: 43, t7_2: 45, t7_3: 50, t7_4: 55, t7_5: 60,
};

export function getSuggestedDate(taskId, targetListDate) {
  if (!targetListDate) return null;
  const offset = TASK_OFFSETS[taskId];
  if (offset === undefined) return null;
  const d = new Date(targetListDate);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

export function getAllTasks() {
  return PHASES.flatMap((p) => p.tasks.map((t) => ({ phaseId: p.id, phaseTitle: p.title, ...t })));
}

export function getTaskById(taskId) {
  return getAllTasks().find((t) => t.id === taskId);
}

export function computeProgress(listing) {
  const all = getAllTasks();
  const done = all.filter((t) => listing?.tasks?.[t.id]?.done).length;
  return { done, total: all.length, pct: all.length ? Math.round((done / all.length) * 100) : 0 };
}

export function computePhaseProgress(listing, phaseId) {
  const phase = PHASES.find((p) => p.id === phaseId);
  if (!phase) return { done: 0, total: 0, pct: 0 };
  const total = phase.tasks.length;
  const done = phase.tasks.filter((t) => listing?.tasks?.[t.id]?.done).length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}
